/** Mixer section — channel strips for each synth instance.
 *
 * Each channel strip shows: MIDI channel badge, synth name (with inline rename),
 * volume slider, and a pan knob. The active synth is highlighted.
 * Add/remove synth actions are integrated into the mixer.
 */

import {
	MoveHorizontal,
	Pencil,
	Piano,
	Plus,
	Trash2,
	Volume2,
	VolumeX,
} from "lucide-react";
import { useCallback, useState } from "react";
import type { PersistedSynthInstance } from "../lib/stateStorage.ts";
import type { MidiChannel } from "../types/synth.ts";
import { MAX_SYNTHS } from "../types/synth.ts";

/** All possible MIDI channel values. */
const MIDI_CHANNELS: readonly MidiChannel[] = [
	"all",
	1,
	2,
	3,
	4,
	5,
	6,
	7,
	8,
	9,
	10,
	11,
	12,
	13,
	14,
	15,
	16,
] as const;

/** Props for the Mixer component. */
export type MixerProps = {
	readonly instances: readonly PersistedSynthInstance[];
	readonly activeId: string;
	readonly onSelect: (id: string) => void;
	readonly onAdd: () => void;
	readonly onRemove: (id: string) => void;
	readonly onRename: (id: string, name: string) => void;
	readonly onMidiChannel: (id: string, channel: MidiChannel) => void;
	readonly onVolumeChange: (id: string, gain: number) => void;
	readonly onPanChange: (id: string, pan: number) => void;
	readonly onMuteToggle: (id: string, muted: boolean) => void;
	readonly onSoloToggle: (id: string, solo: boolean) => void;
};

/** A single channel strip inside the Mixer. */
function ChannelStrip({
	instance,
	isActive,
	canDelete,
	onSelect,
	onRemove,
	onRename,
	onMidiChannel,
	onVolumeChange,
	onPanChange,
	onMuteToggle,
	onSoloToggle,
}: {
	readonly instance: PersistedSynthInstance;
	readonly isActive: boolean;
	readonly canDelete: boolean;
	readonly onSelect: () => void;
	readonly onRemove: () => void;
	readonly onRename: (name: string) => void;
	readonly onMidiChannel: (channel: MidiChannel) => void;
	readonly onVolumeChange: (gain: number) => void;
	readonly onPanChange: (pan: number) => void;
	readonly onMuteToggle: (muted: boolean) => void;
	readonly onSoloToggle: (solo: boolean) => void;
}) {
	const [editing, setEditing] = useState(false);
	const [editName, setEditName] = useState("");

	const startRename = useCallback(() => {
		setEditName(instance.name);
		setEditing(true);
	}, [instance.name]);

	const commitRename = useCallback(() => {
		if (editName.trim()) {
			onRename(editName.trim());
		}
		setEditing(false);
	}, [editName, onRename]);

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: click handles selection
		// biome-ignore lint/a11y/noStaticElementInteractions: converted from button to avoid nested-button error
		<div
			className={`mixer-strip ${isActive ? "active" : ""}`}
			onClick={onSelect}
		>
			{/* MIDI channel badge — above name */}
			<select
				className="mixer-midi-badge"
				value={
					instance.midiChannel === "all" ? "all" : String(instance.midiChannel)
				}
				onChange={(e) => {
					e.stopPropagation();
					const val = e.target.value;
					const channel: MidiChannel =
						val === "all" ? "all" : (Number(val) as MidiChannel);
					onMidiChannel(channel);
				}}
				onClick={(e) => e.stopPropagation()}
				title="MIDI Channel"
			>
				{MIDI_CHANNELS.map((ch) => (
					<option key={String(ch)} value={String(ch)}>
						{ch === "all" ? "All" : `Ch ${ch}`}
					</option>
				))}
			</select>

			{/* Synth name / icon */}
			<div className="mixer-strip-header">
				<Piano size={10} className="mixer-strip-icon" />
				{editing ? (
					<input
						type="text"
						className="mixer-rename-input"
						value={editName}
						onChange={(e) => setEditName(e.target.value)}
						onBlur={commitRename}
						onKeyDown={(e) => {
							if (e.key === "Enter") commitRename();
							if (e.key === "Escape") setEditing(false);
							e.stopPropagation();
						}}
						onClick={(e) => e.stopPropagation()}
						// biome-ignore lint/a11y/noAutofocus: deliberate for rename flow
						autoFocus
					/>
				) : (
					<span className="mixer-strip-name" title={instance.name}>
						{instance.name}
					</span>
				)}
			</div>

			{/* Volume fader */}
			<div className="mixer-fader">
				<Volume2 size={10} className="mixer-fader-icon" />
				<input
					type="range"
					min={0}
					max={1}
					step={0.01}
					value={instance.params.masterGain}
					className="mixer-volume-slider"
					onChange={(e) => {
						e.stopPropagation();
						onVolumeChange(Number(e.target.value));
					}}
					onClick={(e) => e.stopPropagation()}
					aria-label={`${instance.name} volume`}
				/>
				<span className="mixer-volume-value">
					{Math.round(instance.params.masterGain * 100)}%
				</span>
			</div>

			{/* Pan fader */}
			<div className="mixer-fader">
				<MoveHorizontal size={10} className="mixer-fader-icon" />
				<input
					type="range"
					min={-1}
					max={1}
					step={0.01}
					value={instance.params.pan ?? 0}
					className="mixer-volume-slider"
					onChange={(e) => {
						e.stopPropagation();
						onPanChange(Number(e.target.value));
					}}
					onClick={(e) => e.stopPropagation()}
					aria-label={`${instance.name} pan`}
				/>
				<span className="mixer-volume-value">
					{(instance.params.pan ?? 0) === 0
						? "C"
						: (instance.params.pan ?? 0) < 0
							? `L${Math.round(Math.abs(instance.params.pan ?? 0) * 100)}`
							: `R${Math.round((instance.params.pan ?? 0) * 100)}`}
				</span>
			</div>

			{/* Mute / Solo buttons */}
			<div className="mixer-strip-mute-solo">
				<button
					type="button"
					className={`mixer-ms-btn mixer-mute-btn ${instance.muted ? "active" : ""}`}
					onClick={(e) => {
						e.stopPropagation();
						onMuteToggle(!instance.muted);
					}}
					title={instance.muted ? "Unmute" : "Mute"}
				>
					{instance.muted ? <VolumeX size={9} /> : "M"}
				</button>
				<button
					type="button"
					className={`mixer-ms-btn mixer-solo-btn ${instance.solo ? "active" : ""}`}
					onClick={(e) => {
						e.stopPropagation();
						onSoloToggle(!instance.solo);
					}}
					title={instance.solo ? "Unsolo" : "Solo"}
				>
					S
				</button>
			</div>

			{/* Action buttons */}
			<div className="mixer-strip-actions">
				<button
					type="button"
					className="mixer-action-btn"
					onClick={(e) => {
						e.stopPropagation();
						startRename();
					}}
					title="Rename synth"
				>
					<Pencil size={9} />
				</button>
				{canDelete && (
					<button
						type="button"
						className="mixer-action-btn mixer-action-delete"
						onClick={(e) => {
							e.stopPropagation();
							onRemove();
						}}
						title="Remove synth"
					>
						<Trash2 size={9} />
					</button>
				)}
			</div>
		</div>
	);
}

export function Mixer({
	instances,
	activeId,
	onSelect,
	onAdd,
	onRemove,
	onRename,
	onMidiChannel,
	onVolumeChange,
	onPanChange,
	onMuteToggle,
	onSoloToggle,
}: MixerProps) {
	return (
		<div className="mixer">
			<div className="mixer-strips">
				{instances.map((inst) => (
					<ChannelStrip
						key={inst.id}
						instance={inst}
						isActive={inst.id === activeId}
						canDelete={instances.length > 1}
						onSelect={() => onSelect(inst.id)}
						onRemove={() => onRemove(inst.id)}
						onRename={(name) => onRename(inst.id, name)}
						onMidiChannel={(ch) => onMidiChannel(inst.id, ch)}
						onVolumeChange={(gain) => onVolumeChange(inst.id, gain)}
						onPanChange={(pan) => onPanChange(inst.id, pan)}
						onMuteToggle={(muted) => onMuteToggle(inst.id, muted)}
						onSoloToggle={(solo) => onSoloToggle(inst.id, solo)}
					/>
				))}
				{instances.length < MAX_SYNTHS && (
					<button
						type="button"
						className="mixer-strip mixer-strip-add"
						onClick={onAdd}
						title="Add synth"
					>
						<Plus size={14} />
						<span className="mixer-add-label">Add</span>
					</button>
				)}
			</div>
		</div>
	);
}
